import React, { useState, useEffect, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
// eslint-disable-next-line
// @ts-ignore
import * as linkHeaderParser from 'linkheader-parser/dist/linkheader-parser-node';
import ProjectClient from '../../models/projects/projectClient';
import Loader from '../common/loader';
import MobileSelector from './mobileSelector';
import ProjectsSelector from './projectsSelector';
import Card from './card';
import type { Project } from '../../models/projects/project';
import { handleAxiosError } from '../../../common/helpers/axiosHelper';
import { simpleFlashMessage } from '../../../common/utils/flashMessage';

type Props = {
  keyword: string | null;
  order: string | null;
  speficInvestorFlg: boolean;
};

export default function IndexView({
  keyword: initialKeyword,
  order: initialOrder,
  speficInvestorFlg,
}: Props) {
  const projectClient = useRef(new ProjectClient());
  const [projects, setProjects] = useState<Project[]>([]);
  const [keyword, setKeyword] = useState<string | null>(initialKeyword);
  const [order, setOrder] = useState<string | null>(initialOrder);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchProjects = useCallback(() => {
    projectClient.current
      .fetchAll(page, keyword === '' ? null : keyword, order)
      .then(response => {
        const hasNext =
          response.headers &&
          response.headers.hasOwnProperty('link') &&
          linkHeaderParser.parse(response.headers.link).hasOwnProperty('next');
        setProjects(prev => [...prev, ...response.data]);
        setHasNextPage(hasNext);
        setIsLoading(false);
      })
      .catch(error => {
        handleAxiosError(error, simpleFlashMessage);
        setIsLoading(false);
      });
  }, [page, keyword, order]);

  useEffect(() => {
    fetchProjects();
  }, [page, keyword, order]);

  const onLoadMore = () => {
    if (!isLoading && hasNextPage) {
      setPage(prev => prev + 1);
      setIsLoading(true);
    }
  };

  const onChangeKeyword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  const onClickKeywordSearch = () => {
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }
    setProjects([]);
    setPage(1);
    setIsLoading(true);
    setHasNextPage(true);
    setOrder(null);
    typingTimeout.current = setTimeout(() => {
      fetchProjects();
      // history.pushState(null, '', `/projects?keyword=${keyword}`);
    }, 1000);
  };

  const onSubmitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onClickKeywordSearch();
  };

  const onClickOrder = (orderValue: string | null) => {
    if (!isLoading) {
      setProjects([]);
      setPage(1);
      setIsLoading(true);
      setHasNextPage(true);
      setKeyword('');
      setOrder(orderValue);
    }
  };

  return (
    <div>
      <div className="row">
        <div className="col d-block d-lg-none mb-10 justify-content-center">
          <MobileSelector
            order={order}
            keyword={keyword}
            onChangeKeyword={onChangeKeyword}
            onClickOrder={onClickOrder}
            onClickKeywordSearch={onClickKeywordSearch}
          />
        </div>
        <div className="col-lg-3 d-none d-lg-block">
          <ProjectsSelector
            order={order}
            keyword={keyword}
            onChangeKeyword={onChangeKeyword}
            onClickOrder={onClickOrder}
            onClickKeywordSearch={onClickKeywordSearch}
            onSubmitSearch={onSubmitSearch}
          />
        </div>
        <div className="col-lg-9 main-row__wrapper">
          <InfiniteScroll
            pageStart={0}
            loadMore={onLoadMore}
            hasMore={hasNextPage}
            loader={<Loader key={0} type="water" />}
            className="row"
          >
            {projects.map(project => (
              <Card
                key={project.id}
                project={project}
                speficInvestorFlg={speficInvestorFlg}
              />
            ))}
          </InfiniteScroll>
          {!isLoading && projects.length === 0 && (
            <div>プロジェクトは見つかりませんでした</div>
          )}
        </div>
        <div className="col d-block d-lg-none">
          <div className="project-selector__attention">
            <span>※</span>
            <span>
              募集ページについては、募集期間終了後、募集の成立不成立に関わらず事業者の要請に応じて非開示となることがあります。ご了承下さい。
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
